import { create } from 'zustand';
import { TAiAgentDetails, TErrorType, TRouteConfig } from '@core/shared/types';
import useMessageStore from './useMessageStore';
import { logger } from '@shared/logger';

type TErrorState = {
    message: string | null;
    type: TErrorType | null; // Type of the error (TErrorType)
};

export const NEW_CHAT_THREAD_KEY = '__new_chat__';

export type TPendingGeneration = {
    threadId: string | null;
    messageText: string;
};

export const threadKeyOf = (threadId: string | null | undefined): string =>
    threadId || NEW_CHAT_THREAD_KEY;

export type TChatStore = {
    routeConfig: TRouteConfig;
    presentationMode: boolean;
    threadId: string | null;
    isLoading: boolean;
    loadingStates: Record<string, boolean>;
    error: string | null;
    errorType: TErrorType | null;
    errorStates: Record<string, TErrorState>;
    isDeepResearch: boolean;
    setIsDeepResearch: (value: boolean) => void;
    aiAgent: TAiAgentDetails | undefined;
    setAIAgent: (agent: TAiAgentDetails | undefined) => void;
    isPreview: boolean;
    setIsPreview: (value: boolean) => void;

    pendingGenerations: Record<string, TPendingGeneration>;

    startGeneration: (requestId: string, threadId: string | null, messageText: string) => void;

    resolveGenerationThread: (requestId: string, threadId: string) => void;

    finishGeneration: (requestId: string) => void;

    /**
     * Sets the loading state for a specific condition.
     * If any condition is loading, the global `isLoading` state will be set to `true`.
     * @param {string} condition - The name or identifier for the loading condition.
     * @param {boolean} isLoading - The loading state to set for the condition.
     * @returns {void}
     */
    setLoadingState: (condition: string, isLoading: boolean) => void;

    /**
     * Sets the current thread ID.
     * @param {string | null} threadId - The thread ID to set, or `null` to clear it.
     * @returns {void}
     */
    setThreadId: (threadId: string | null) => void;

    /**
     * Sets an error message for a specific condition, including the error type.
     * @param {string} condition - The condition or context where the error occurred.
     * @param {string | null} error - The error message to set, or `null` to clear the error.
     * @param {TErrorType | null} [errorType=TErrorType.ERROR] - The type of the error (default is `ERROR`).
     * @returns {void}
     */
    setError: (condition: string, error: string | null, errorType?: TErrorType | null) => void;

    /**
     * Sets the route configuration for the chat, which includes API client and other settings.
     * @param {TRouteConfig} routeConfig - The route configuration to set for the chat.
     * @returns {void}
     */
    setRouteConfig: (routeConfig: TRouteConfig) => void;

    /**
     * Resets the error states and clears the current error message.
     * @returns {void}
     */
    resetError: () => void;

    /**
     * Toggles the presentation mode for the ui.
     * @param presentationMode
     */
    setPresentationMode: (presentationMode: boolean) => void;
};

const SCOPE_NAME = 'ChatStore'; // Define the scope constant

/**
 * Zustand store for managing the chat state, including loading states, errors, thread information, and route configuration.
 */
const useChatStore = create<TChatStore>()((set) => ({
    routeConfig: {} as TRouteConfig,
    presentationMode: false,
    threadId: null,
    isLoading: true,
    loadingStates: {},
    error: null,
    errorType: null,
    errorStates: {},
    isDeepResearch: false,
    setIsDeepResearch: (value: boolean) => set({ isDeepResearch: value }),
    aiAgent: undefined,
    setAIAgent: (agent: TAiAgentDetails | undefined) => set({ aiAgent: agent }),
    isPreview: false,
    setIsPreview: (value: boolean) => set({ isPreview: value }),

    pendingGenerations: {},

    startGeneration: (requestId: string, threadId: string | null, messageText: string): void => {
        logger.log(SCOPE_NAME, 1, 'startGeneration', requestId, threadId);
        set((state) => ({
            pendingGenerations: {
                ...state.pendingGenerations,
                [requestId]: { threadId, messageText },
            },
        }));
    },

    resolveGenerationThread: (requestId: string, threadId: string): void => {
        set((state) => {
            const pending = state.pendingGenerations[requestId];
            if (!pending) return state;

            return {
                pendingGenerations: {
                    ...state.pendingGenerations,
                    [requestId]: { ...pending, threadId },
                },
            };
        });
    },

    finishGeneration: (requestId: string): void => {
        logger.log(SCOPE_NAME, 1, 'finishGeneration', requestId);
        set((state) => {
            const { [requestId]: removed, ...rest } = state.pendingGenerations;
            if (!removed) return state;
            return { pendingGenerations: rest };
        });
    },

    /**
     * Sets the loading state for a given condition. Updates the `isLoading` state if any condition is loading.
     * @param {string} condition - The name of the condition.
     * @param {boolean} isLoading - Whether the condition is loading.
     * @returns {void}
     */
    setLoadingState: (condition: string, isLoading: boolean): void =>
        set((state) => {
            const updatedLoadingStates = {
                ...state.loadingStates,
                [condition]: isLoading,
            };

            const allResolved = Object.values(updatedLoadingStates).every((value) => !value);

            // Log the loading state change if log level is set

            logger.log(
                SCOPE_NAME,
                3,
                'setLoadingState called with condition:',
                condition,
                'isLoading:',
                isLoading,
            );

            return {
                loadingStates: updatedLoadingStates,
                isLoading: !allResolved, // If any condition is still loading, set `isLoading` to true
            };
        }),

    /**
     * Sets the route configuration for the chat, including the API client and other settings.
     * This also refreshes the token and clears the message store.
     * @param {TRouteConfig} routeConfig - The route configuration object to set.
     * @returns {void}
     */
    setRouteConfig: (routeConfig: TRouteConfig) => {
        useMessageStore.getState().clearMessages();

        routeConfig.apiClient.setTenantKey(routeConfig.apiTenantKey);
        routeConfig.apiClient.setCategory(routeConfig.apiPath);

        // Log the route config being set
        logger.log(SCOPE_NAME, 1, 'save RouteConfig', routeConfig);

        set(() => ({ routeConfig }));
    },

    /**
     * Sets an error for a specific condition and logs it.
     * @param {string} condition - The condition where the error occurred.
     * @param {string | null} error - The error message, or `null` to clear the error.
     * @param {TErrorType | null} [errorType=TErrorType.ERROR] - The type of error (default is `ERROR`).
     * @returns {void}
     */
    setError: (
        condition: string,
        error: string | null,
        errorType: TErrorType | null = TErrorType.ERROR,
    ): void =>
        set((state) => {
            const updatedErrorStates = {
                ...state.errorStates,
                [condition]: {
                    message: error,
                    type: errorType, // Use errorType instead of errorCode
                },
            };

            const firstErrorState = Object.values(updatedErrorStates).find(
                (errorState) => errorState.message !== null,
            );

            if (error) {
                logger.log(
                    SCOPE_NAME,
                    1,
                    'setError called with condition:',
                    condition,
                    'error:',
                    error,
                    'errorType:',
                    errorType, // Log errorType
                );
            }

            return {
                errorStates: updatedErrorStates,
                // Prefer the newest message/type that was just set to avoid mismatched title/body.
                error: error !== null ? error : firstErrorState?.message || null,
                errorType: error !== null ? errorType : firstErrorState?.type || null,
            };
        }),

    /**
     * Resets the error state and clears all error messages.
     * @returns {void}
     */
    resetError: (): void => {
        logger.log(SCOPE_NAME, 1, 'resetError & Hide Toast called');
        set({ error: null, errorType: null, errorStates: {} });
    },

    /**
     * Sets the thread ID for the chat and clears the message store.
     * @param {string | null} threadId - The thread ID to set, or `null` to clear it.
     * @returns {void}
     */
    setThreadId: (threadId: string | null): void => {
        logger.log(SCOPE_NAME, 1, 'setThreadId called with threadId:', threadId); // Log thread ID change
        set({ threadId });
    },

    /**
     * Toggles the presentation mode for the ui.
     * @returns {void}
     */
    setPresentationMode: (presentationMode: boolean): void => {
        logger.log(
            SCOPE_NAME,
            1,
            'setPresentationMode called with presentationMode:',
            presentationMode,
        );
        set({ presentationMode });
    },
}));

export const isThreadGenerating = (
    pendingGenerations: Record<string, TPendingGeneration>,
    threadId: string | null | undefined,
): boolean =>
    Object.values(pendingGenerations).some(
        (pending) => threadKeyOf(pending.threadId) === threadKeyOf(threadId),
    );

export const getPendingGenerationForThread = (
    pendingGenerations: Record<string, TPendingGeneration>,
    threadId: string | null | undefined,
): TPendingGeneration | undefined =>
    Object.values(pendingGenerations).find(
        (pending) => threadKeyOf(pending.threadId) === threadKeyOf(threadId),
    );

export default useChatStore;
